import { useQuery, useQueryClient } from '@tanstack/react-query'
import { DataTable, useDataTableContext } from 'components/DataTable'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { Col, Row } from 'react-bootstrap'
import useApiService from 'services/api'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import * as S from '../CommonTable.styles'

import to from 'await-to-js'
import { AxiosError } from 'axios'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import TableButtonProduct from 'components/Buttons/TableButtons/TableButtonProduct'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import DistributorAssignModal from 'components/Modals/AssignmentModal/DistributorAssignModal'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import DoorExportModal from 'components/Modals/ExportModal/DoorExportModal'
import OpportunitiesTableFooterControls from 'components/Opportunities/OpportunitiesTableFooterControls'
import { DoorsFilterset } from 'components/Tables/DoorsTable/DoorsFilterset'
import { DOOR_SUMMARY_KEY } from 'constants/tableQueryKeys'
import {
  PiCalendarStar,
  PiExport,
  PiForkKnife,
  PiHandshake,
  PiShippingContainer,
  PiStorefront,
  PiTag,
  PiTruck,
} from 'react-icons/pi'
import { notifyError } from 'services/toast'
import { useSelectedProductIds } from 'stores/SelectedProductsStore/SelectedProductsStore'
import { paramsContainAccountFilters } from 'utils/filterUtils'
import { useOpportunitiesContext } from '../../../pages/app/opportunities/opportunities-context'

import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { DataTableContainer } from '../../DataTable/UI'

import { TableSearchWithElastic } from '../../Filters/TableSearchWithElastic'
import { DoorMenuModal } from '../../Modals/MenuModal/DoorMenuModal'
import { cn } from '../../UI/cn'
import { ChangeProductViewModal } from '../../change-product-view-modal'
import { ManageTagsDistributorsParams } from 'services/common_table_actions'
import { SiSalesforce } from 'react-icons/si'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { toast } from 'react-toastify'
import { CreateDealModalForTable } from 'components/Deals/CreateDealModal/CreateDealModal'
import GPOAssignModal from 'components/Modals/AssignmentModal/GPOAssignModal'
import {
  sortableFieldOptions,
  sortableFields,
  useDoorsTableColumnsV2,
} from './DoorsTableColumnsV2'
import TableButtonSort from 'components/Buttons/TableButtons/TableButtonSort'
import { IDoorsTableProps } from './DoorsTable'
import { PlacesSummary } from 'models/summaries'
import ReminderModal from 'components/Reminders/ReminderModal'
import { useUnifiedDoorsReportsTable } from 'services/opportunities/useUnifiedDoorsReportsTable'
import { getTargetType } from 'services/opportunities/targetTypeMap'

const PAGE_SIZE = 100

export function DoorsTableComponentV2({
  hideTitle = true,
  ...props
}: IDoorsTableProps) {
  const featureFlag = useFeatureFlagService()
  const tableContext = useDataTableContext()
  const queryClient = useQueryClient()
  const api = useApiService()
  const isChainTable = props.chainFilter != null

  const { selectedTag } = useOpportunitiesContext()
  const [menuModal, setMenuModal] = useState<
    | {
        doorId: number
        doorName?: string
        itemsIds?: number[]
        menuLength?: number
      }
    | undefined
  >(undefined)

  // TABLE CONTEXT (SORTING, ETC)
  const { sorting, setSorting } = dataTableSortingStoreRepo.getStore(
    props.tableKey
  )()
  const {
    state: { rowSelection, totalSelectedRows, isAllRowsSelected },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = tableContext

  const selectedProductsIds = useSelectedProductIds()

  const [showProductSelection, setShowProductSelection] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showAssignTags, setShowAssignTags] = useState(false)
  const [showCreateDealModal, setShowCreateDealModal] = useState(false)
  const [showAssignDistributors, setShowAssignDistributors] = useState(false)
  const [showAssignGPOs, setShowAssignGPOs] = useState(false)
  const [showExportAllModal, setShowExportAllModal] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)

  const restaurantsFilterStore = filterStoreRepo.getStore('DOORS_TABLE')
  const { page, setPage, search } = restaurantsFilterStore()
  const filtersAsParams = useFilterParams(restaurantsFilterStore)

  // Set default sort to chain count descending if no sort is set
  useEffect(() => {
    if (!sorting.length) {
      setSorting([{ id: 'chain_count', desc: true }])
    }
    // Only run on mount
  }, [])

  // FILTER PARAMS (FOR API CALL)
  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = { ...filtersAsParams }

    if (props.chainFilter) {
      params.chain = props.chainFilter
    }

    return params
  }, [filtersAsParams, props.chainFilter])

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  // Check if any elastic search is active (name or menu)
  const hasActiveSearch = useMemo(() => {
    return Object.keys(search).some((key) => search[key]?.value)
  }, [search])

  // Reset relevancy sort to chain_count when search is cleared
  useEffect(() => {
    if (!hasActiveSearch && sorting.length && sorting[0].id === 'relevancy') {
      setSorting([{ id: 'chain_count', desc: true }])
    }
  }, [hasActiveSearch, sorting, setSorting])

  // Filter sort options - only show relevancy when a search is active
  const filteredSortOptions = useMemo(() => {
    if (hasActiveSearch) {
      return sortableFieldOptions
    }
    return sortableFieldOptions.filter((opt) => opt.value !== 'relevancy')
  }, [hasActiveSearch])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
    }),
    [filterParams, sortParams]
  )

  // FIR-6995: behind enable_unified_opps_endpoint, read rows + count from the
  // unified /opportunities/doors/ endpoint (target_type=restaurant).
  const unifiedDoors = useUnifiedDoorsReportsTable(filterAndSortParams)

  // FIR-7822: when the unified doors list is active, bulk actions must
  // re-resolve a select-all through the SAME unified engine. Route them to the
  // doors-scoped `opportunities/doors/...` endpoints for this target type;
  // undefined keeps the legacy endpoints when the flag is off.
  const unifiedTargetType = unifiedDoors.enabled
    ? getTargetType('restaurant')
    : undefined

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    page: page,
    products: selectedProductsIds,
    ...(unifiedDoors.enabled ? { unified: true } : {}),
  })

  // API CALL
  const {
    data: places,
    isFetching,
    refetch,
  } = useQuery({
    staleTime: Infinity,
    queryKey: TABLE_QUERY_KEY,
    enabled: selectedTag === 'fb-locations' || !!props.chainFilter, // Enable only when tab is selected or chain filter is present (chain page)
    queryFn: async ({ signal }) => {
      clearSelectedRows()
      const unifiedList = unifiedDoors.getListAction()
      const [err, res] = await to(
        unifiedList
          ? unifiedList(
              {
                ...filterAndSortParams,
                page: page,
                limit: PAGE_SIZE,
                product_id: selectedProductsIds,
              },
              signal
            )
          : api.getDoorsWithCRMReports(
              {
                ...filterAndSortParams,
                page: page,
                limit: PAGE_SIZE,
                product_id: selectedProductsIds,
              },
              signal
            )
      )

      if ((err as AxiosError)?.code === 'ERR_NETWORK') {
        notifyError('Sorry! Unable to complete this search, please try again.')
      }
      if (err) throw err

      return res.results
    },
  })

  useEffect(() => {
    clearSelectedRows()
  }, [places])

  // SUMMARY API CALL
  // Only fetch summary when doors tab is selected or chain filter is present (chain page)
  const {
    data: placesSummary = {
      total_ltv: 0,
      chains_count: 0,
      places_count: 0,
    } as PlacesSummary,
    isFetching: placesSummaryLoading,
  } = api.useGetDoorReportsSummary(
    {
      ...filterParams,
      product_id: selectedProductsIds.filter((p) => p !== -1),
    },
    undefined,
    selectedTag === 'fb-locations' || !!props.chainFilter
  )

  // When the unified swap is on, the door (row) count comes from the unified
  // doors summary; the rich LTV/chains stats stay on the legacy V2 summary
  // (unified does not provide them yet) and fall back to 0 until they do.
  const effectiveDoorCount = unifiedDoors.enabled
    ? (unifiedDoors.unifiedCount ?? placesSummary.places_count ?? 0)
    : (placesSummary.places_count ?? 0)

  // ˆ PASS LTV RESULTS TO PARENT COMPONENT ˆ
  useEffect(() => {
    props.setDoorsSummary?.({
      ...placesSummary,
      places_count: effectiveDoorCount,
      isLoading: placesSummaryLoading,
    })
    setTotalRowsInBackend(effectiveDoorCount)
  }, [placesSummaryLoading, placesSummary, effectiveDoorCount])

  const selectedDoorIds = useMemo(() => {
    const accessor = 'restaurant.firstbite_id'
    return (
      places
        ?.filter((_, i: number) => rowSelection[i])

        ?.map((r: any) => {
          return accessor.split('.').reduce((acc, key) => acc[key], r)
        }) ?? []
    )
  }, [places, rowSelection])

  const unselectedDoorIds = useMemo(() => {
    if (!isAllRowsSelected) return []
    return (
      places
        ?.filter((_, i: number) => !rowSelection[i])
        ?.map((r: any) => r.restaurant.firstbite_id) ?? []
    )
  }, [places, rowSelection, isAllRowsSelected])

  const columns = useDoorsTableColumnsV2(
    setMenuModal,
    !!isChainTable,
    props.chainFilter,
    selectedProductsIds,
    sorting,
    false
  )

  const selectedDoorProxy = useMemo(() => {
    if (selectedDoorIds.length !== 1 || !places) return undefined
    const doorProxy = places.find(
      (item) => item.restaurant.firstbite_id === selectedDoorIds[0]
    )
    return doorProxy
  }, [selectedDoorIds, places])

  const refetchDoorsSummary = useCallback(() => {
    if (paramsContainAccountFilters(filterAndSortParams)) {
      queryClient.refetchQueries({ queryKey: [DOOR_SUMMARY_KEY] })
    }
  }, [filterAndSortParams, queryClient])

  const apiTagsAction = useCallback(
    async (values: ManageTagsDistributorsParams) => {
      const response = await api.commonManageTags({
        values,
        tableType: 'doors-reports-table',
        unifiedTargetType,
        unifiedDoors: true,
      })
      refetchDoorsSummary()
      return response
    },
    [api, refetchDoorsSummary, unifiedTargetType]
  )

  const apiDistributorsAction = useCallback(
    async (values: ManageTagsDistributorsParams) => {
      const response = await api.commonManageDistributors({
        tableType: 'doors-reports-table',
        values,
        unifiedTargetType,
        unifiedDoors: true,
      })
      refetchDoorsSummary()
      return response
    },
    [api, refetchDoorsSummary, unifiedTargetType]
  )

  const apiGPOsAction = useCallback(
    async (values: ManageTagsDistributorsParams) => {
      const response = await api.commonManageGPOs({
        tableType: 'doors-reports-table',
        values,
        unifiedTargetType,
        unifiedDoors: true,
      })
      refetchDoorsSummary()
      return response
    },
    [api, refetchDoorsSummary, unifiedTargetType]
  )

  const linkVariant = useTableLinkVariant()
  const bulkActions = useMemo(
    () => [
      {
        label: 'Create Deals',
        icon: <PiHandshake size={20} />,
        callback: () => setShowCreateDealModal(true),
      },
      {
        label: 'Manage Tags',
        icon: <PiTag size={20} />,
        callback: () => setShowAssignTags(true),
      },
      {
        label: 'Add Known Distributors',
        icon: <PiTruck size={20} />,
        callback: () => setShowAssignDistributors(true),
      },
      {
        label: 'Add GPO',
        icon: <PiShippingContainer size={20} />,
        callback: () => setShowAssignGPOs(true),
      },
      {
        label: 'Schedule Reminder',
        icon: <PiCalendarStar size={20} />,
        callback: () => setShowReminderModal(true),
        disabled: selectedDoorIds.length !== 1,
        disabledTooltipText:
          'Please select exactly one door to schedule a reminder',
      },
      {
        label: 'Export Selected',
        icon: <PiExport size={20} />,
        callback: () => setShowExportModal(true),
      },
      {
        label: 'Push to Salesforce',
        icon: <SiSalesforce size={20} />,
        hidden: !featureFlag.enablePushToSalesforce,
        callback: () => {
          setTimeout(() => {
            toast.success('Successfully pushed to Salesforce')
          }, 1000)
        },
      },
    ],
    [selectedDoorIds.length, featureFlag.enablePushToSalesforce]
  )

  return (
    <DataTableContainer>
      <DataTable
        tableHeader={
          <>
            {!hideTitle && (
              <Row className="mb-4">
                <Col>
                  <S.CardTitle>
                    Doors{' '}
                    <span style={{ fontWeight: 100 }}>
                      ({effectiveDoorCount})
                    </span>
                  </S.CardTitle>
                </Col>
              </Row>
            )}
            <S.SearchContainer>
              <div className="flex w-full gap-2 items-center">
                <TableSearchWithElastic
                  filterStore={restaurantsFilterStore}
                  historyKey="doors"
                  options={[
                    {
                      label: 'Menu',
                      searchKey: 'menu',
                      searchPlaceholder: 'Search menus… e.g. chicken',
                      elastic: true,
                      autoSetRelevancySort: false,
                      icon: PiForkKnife,
                    },
                    {
                      label: 'Name',
                      searchKey: 'restaurant_name',
                      searchPlaceholder:
                        'Search restaurant names… e.g. Bella’s Bistro',
                      elastic: true,
                      autoSetRelevancySort: true,
                      icon: PiStorefront,
                    },
                  ]}
                  onSearchApplied={(option) => {
                    if (option.autoSetRelevancySort) {
                      // Name searches: set to relevancy sort
                      setSorting([{ id: 'relevancy', desc: true }])
                    } else {
                      // All other searches (menu, domain): reset to chain count
                      setSorting([{ id: 'chain_count', desc: true }])
                    }
                  }}
                />

                <TableButtonProduct
                  selectedProducts={selectedProductsIds}
                  onClick={() => setShowProductSelection(true)}
                />

                <DoorsFilterset filterIdentifier="DOORS_TABLE" />

                <ColumnSelectorRecipient tableKey={props.tableKey} />
                <TableButtonSort
                  options={filteredSortOptions}
                  onSelect={(value, direction) => {
                    return setSorting([
                      { id: value, desc: direction === 'desc' },
                    ])
                  }}
                  selectedValue={
                    sorting.length
                      ? {
                          value: sorting[0].id,
                          direction: sorting[0].desc ? 'desc' : 'asc',
                        }
                      : undefined
                  }
                />
              </div>

              <div className={cn('flex flex-row justify-between gap-4')}>
                {linkVariant !== 'plain' && (
                  <TableButtonAction
                    items={bulkActions}
                    disabled={!totalSelectedRows}
                    selectedRowsCount={totalSelectedRows ?? 0}
                  />
                )}
                <FilterChips
                  identifier={'DOORS_TABLE'}
                  clearAllButton
                  showActive
                  store={restaurantsFilterStore}
                />
              </div>
            </S.SearchContainer>

            <TagAssignModal
              show={showAssignTags}
              handleClose={() => setShowAssignTags(false)}
              tableQueryKey={TABLE_QUERY_KEY}
              data={places ?? []}
              filterAndSortParams={filterAndSortParams}
              apiTagsAction={apiTagsAction}
              idAccessor="door_proxy_id"
              tableObjAccessor="door_taglist"
            />

            <DistributorAssignModal
              show={showAssignDistributors}
              handleClose={() => setShowAssignDistributors(false)}
              tableQueryKey={TABLE_QUERY_KEY}
              data={places ?? []}
              idAccessor="door_proxy_id"
              filterAndSortParams={filterAndSortParams}
              apiDistributorsAction={apiDistributorsAction}
              tableObjAccessor="door_distributors"
            />

            <GPOAssignModal
              show={showAssignGPOs}
              handleClose={() => setShowAssignGPOs(false)}
              tableQueryKey={TABLE_QUERY_KEY}
              data={places ?? []}
              idAccessor="door_proxy_id"
              filterAndSortParams={filterAndSortParams}
              apiGPOsAction={apiGPOsAction}
              tableObjAccessor="door_gpos"
            />

            <CreateDealModalForTable
              optimisticUiTarget="none"
              show={showCreateDealModal}
              handleClose={() => setShowCreateDealModal(false)}
              onDealCreated={refetch}
              queryKey={TABLE_QUERY_KEY}
              filterAndSortParams={filterAndSortParams}
              data={places ?? []}
              idAccessor="chain_proxy.id"
              doorIdAccessor="door_proxy_id"
              companyTypeSlug={'restaurants-bars'}
              tableTypeSlug={'doors-reports-table'}
              forDoorSelection={true}
              unifiedTargetType={unifiedTargetType}
              unifiedDoors={true}
            />
          </>
        }
        tableKey={props.tableKey}
        selectHeaderActions={
          linkVariant === 'plain' ? (
            <TableButtonAction
              items={bulkActions}
              disabled={!totalSelectedRows}
              selectedRowsCount={totalSelectedRows ?? 0}
            />
          ) : undefined
        }
        data={places ?? []}
        loading={isFetching}
        columns={columns}
        sortableFields={[...sortableFields]}
        virtualizeRows={true}
        enableRowSelection={!props.chainFilter}
        enableColumnResizing
        rowHeight={44}
        headerHeight={40}
        stickyFirstColumn={linkVariant === 'plain'}
        isPaginationEnabled={true}
        defaultSort={[{ id: 'chain_count', desc: true }]}
        paginationOptions={{
          totalRows: effectiveDoorCount,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: placesSummaryLoading,
        }}
        selectAllText="Select Targets"
        footerControls={
          <OpportunitiesTableFooterControls
            companyTypeSlug={'restaurants-bars'}
            chainProxy={props.chainProxy}
            unmatchedTableKey={props.unmatchedTableKey}
            onDownloadButtonClick={() =>
              selectedTag === 'fb-companies'
                ? setShowExportAllModal(true)
                : setShowExportModal(true)
            }
          />
        }
      />

      <ChangeProductViewModal
        show={showProductSelection}
        handleClose={() => setShowProductSelection(false)}
      />

      {menuModal?.doorId && (
        <DoorMenuModal
          filterStore={restaurantsFilterStore}
          show={!!menuModal}
          handleClose={() => setMenuModal(undefined)}
          doorId={menuModal.doorId!}
          doorName={menuModal.doorName!}
          menuLength={menuModal?.menuLength}
        />
      )}

      {featureFlag.enableReminders && (
        <ReminderModal
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          defaultDoorProxyId={selectedDoorProxy?.door_proxy_id}
          defaultCompanyId={selectedDoorProxy?.chain_proxy?.id}
          onCreated={() => {
            setShowReminderModal(false)
          }}
        />
      )}

      <DoorExportModal
        tableTypeSlug="doors-reports-table"
        filters={filterAndSortParams}
        productSelected={selectedProductsIds[0]}
        show={showExportModal || !!showExportAllModal}
        _handleClose={() => {
          setShowExportModal(false)
          setShowExportAllModal?.(false)
        }}
        selectedIds={
          showExportModal && !isAllRowsSelected ? selectedDoorIds : undefined
        }
        excludeIds={isAllRowsSelected ? unselectedDoorIds : undefined}
        count={showExportModal ? selectedDoorIds?.length : effectiveDoorCount}
      />
    </DataTableContainer>
  )
}
